import { useContext } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { UserContext } from "app/contexts/UserContext";
import { markOptionalItem } from "app/actions/academySetup";
import {
  billingMethodOf,
  billingMethodSource,
  offlineChoiceSkipsStripe,
} from "app/components/AcademySetup/items";
import { PaymentGuardChoice } from "app/routes/Billing/NewOffering/PaymentGuardModal";
import { trackBillingMethodSelected } from "app/utils/analytics-helpers/academySetup";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import useCurrentCoachProfile from "./scheduling/useCurrentCoachProfile";
import useAcademySetupItems from "./useAcademySetupItems";
import useActions from "./useActions";

/**
 * The payment collection guard for a new package or subscription (spec §5,
 * Figma 531:14813), shared by every path that opens one of those forms: the
 * offering chooser and the Billing list's Clone action. The choice is
 * permanent per object, so the guard asks on every creation while Stripe is
 * unconnected — the state where the form's own field has Online disabled
 * and no way to fix it in place. With Stripe connected the form's field
 * offers the real choice itself, Online preset, so the guard is skipped.
 */
export default function usePaymentGuard(facilityId: string | null): {
  /** Academy coach: the only accounts the guard applies to. */
  academyCoach: boolean;
  /** Whether the form should be reached through the guard. */
  required: boolean;
  stripeConnected: boolean;
  /**
   * Whether the Stripe status has settled. "Known" is the request having
   * settled, not the profile being present: fetchSelf stores an empty
   * facility when the scheduling GET fails (and never retries), so waiting
   * on the profile itself would never resolve. An absent profile then reads
   * as unconnected and the guard offers offline only, the same fallback the
   * offering forms apply.
   */
  stripeStatusKnown: boolean;
  /** Side effect of the coach's answer; the caller then opens the form. */
  confirm: (choice: PaymentGuardChoice) => void;
} {
  const self = useSelector(
    (state: RootState) => state.user.self,
    shallowEqual,
  );
  const ctas = useSelector(
    (state: RootState) => state.academySetup.ctas,
    shallowEqual,
  );
  const user = useContext(UserContext);
  const { data: coachProfile } = useCurrentCoachProfile();
  const setup = useAcademySetupItems();
  const setupItems = setup.loaded ? setup.items : null;
  const actions = useActions({ markOptionalItem });

  const academyCoach = isAcademyOnboardingCoach(self);
  const stripeConnected = Boolean(
    facilityId
      ? user.facility?.stripeIntegrationEnabled
      : coachProfile?.stripeIntegrationEnabled,
  );
  const stripeStatusKnown = facilityId
    ? Boolean(user.facility)
    : self?.scheduling !== undefined;

  const confirm = (choice: PaymentGuardChoice) => {
    // Spec §6.2 billing_method_selected: the guard IS the explicit
    // collection-method choice (plan B2), and it asks on every creation, so
    // every Confirm is reported; distinct users are the analyst's to count.
    trackBillingMethodSelected(billingMethodOf(choice), billingMethodSource(ctas));
    if (
      choice === "offline" &&
      offlineChoiceSkipsStripe(setupItems, stripeConnected)
    ) {
      // The coach's decision not to take online payments: the Stripe
      // checklist item completes as skipped, as if its own skip link was used.
      actions.markOptionalItem("stripe_connect", "skipped");
    }
  };

  return {
    academyCoach,
    required: academyCoach && !stripeConnected,
    stripeConnected,
    stripeStatusKnown,
    confirm,
  };
}
